import {
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  InteractionRequiredAuthErrorCodes,
} from '@azure/msal-browser';

/**
 * Expected error codes during silent token renewal (refresh or acquisition).
 * When these occur, user interaction or network reconnection is required;
 * the failure is handled gracefully by returning null and allowing the 401
 * interceptor to trigger interactive re-authentication. They are normal auth
 * lifecycle events, not actionable application crashes.
 */
const EXPECTED_SILENT_AUTH_ERROR_CODES = new Set<string>([
  BrowserAuthErrorCodes.postRequestFailed,
  BrowserAuthErrorCodes.timedOut,
  BrowserAuthErrorCodes.noNetworkConnectivity,
  BrowserAuthErrorCodes.getRequestFailed,
  BrowserAuthErrorCodes.iframeClosedPrematurely,
  BrowserAuthErrorCodes.userCancelled,
  BrowserAuthErrorCodes.interactionInProgress,
  InteractionRequiredAuthErrorCodes.interactionRequired,
  InteractionRequiredAuthErrorCodes.consentRequired,
  InteractionRequiredAuthErrorCodes.loginRequired,
  InteractionRequiredAuthErrorCodes.refreshTokenExpired,
  InteractionRequiredAuthErrorCodes.badToken,
  InteractionRequiredAuthErrorCodes.noTokensFound,
]);

const EXPECTED_SILENT_AUTH_CODE_OR_MESSAGE_PATTERN =
  /post_request_failed|timed_out|no_network_connectivity|get_request_failed|iframe_closed_prematurely|interaction_required|consent_required|login_required|refresh_token_expired/i;

/**
 * Returns true if an error from silent token renewal represents an expected
 * operational condition (network drop, background throttling timeout, or
 * natural session/token expiration requiring user interaction).
 */
export function isExpectedSilentAuthError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;

  if (
    err instanceof InteractionRequiredAuthError ||
    ('name' in err && (err as { name?: unknown }).name === 'InteractionRequiredAuthError')
  ) {
    return true;
  }

  const errorCode = 'errorCode' in err && typeof (err as { errorCode?: unknown }).errorCode === 'string'
    ? (err as { errorCode: string }).errorCode
    : null;

  if (errorCode && (
    EXPECTED_SILENT_AUTH_ERROR_CODES.has(errorCode) ||
    EXPECTED_SILENT_AUTH_CODE_OR_MESSAGE_PATTERN.test(errorCode)
  )) {
    return true;
  }

  const message = 'message' in err && typeof (err as { message?: unknown }).message === 'string'
    ? (err as { message: string }).message
    : null;

  if (message && EXPECTED_SILENT_AUTH_CODE_OR_MESSAGE_PATTERN.test(message)) {
    return true;
  }

  return false;
}
